/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { AlertKind } from '@typescript-kit/core';
import { ViewComponentKey } from '../key/view-component.key';
import { ComponentData, ComponentModel, ComponentFieldKey } from '../model/component.model';

export interface AlertData extends ComponentData {
  kind?: AlertKind;
  message?: string;
  isPinned?: boolean;
  isClosable?: boolean;
  onSetTimeout?: (model: AlertData) => void;
  onClearTimeout?: (model: AlertData) => void;
  onDiscard?: (model: AlertData) => void;
}

export class AlertFieldKey {
  static readonly KIND = 'kind';
  static readonly MESSAGE = 'message';
  static readonly IS_PINNED = 'isPinned';
  static readonly IS_CLOSABLE = 'isClosable';
  static readonly ON_SET_TIMEOUT = 'onSetTimeout';
  static readonly ON_CLEAR_TIMEOUT = 'onClearTimeout';
  static readonly ON_DISCARD = 'onDiscard';
}

export class AlertModel extends ComponentModel implements AlertData {
  static readonly KEYS = ComponentModel.KEYS.concat(
    Object.keys(AlertFieldKey).map((key) => AlertFieldKey[key])
  );

  constructor(data?: AlertData, keys?: string[]) {
    super(data, keys || AlertModel.KEYS);
  }

  get kind(): AlertKind {
    return this.get(AlertFieldKey.KIND);
  }

  set kind(value: AlertKind) {
    this.set(AlertFieldKey.KIND, value);
  }

  get message(): string {
    return this.get(AlertFieldKey.MESSAGE);
  }

  set message(value: string) {
    this.set(AlertFieldKey.MESSAGE, value);
  }

  get isPinned(): boolean {
    return this.get(AlertFieldKey.IS_PINNED);
  }

  set isPinned(value: boolean) {
    this.set(AlertFieldKey.IS_PINNED, value);
  }

  get isClosable(): boolean {
    return this.get(AlertFieldKey.IS_CLOSABLE);
  }

  set isClosable(value: boolean) {
    this.set(AlertFieldKey.IS_CLOSABLE, value);
  }

  get onSetTimeout(): (model: AlertData) => void {
    return this.get(AlertFieldKey.ON_SET_TIMEOUT);
  }

  set onSetTimeout(value: (model: AlertData) => void) {
    this.set(AlertFieldKey.ON_SET_TIMEOUT, value);
  }

  get onClearTimeout(): (model: AlertData) => void {
    return this.get(AlertFieldKey.ON_CLEAR_TIMEOUT);
  }

  set onClearTimeout(value: (model: AlertData) => void) {
    this.set(AlertFieldKey.ON_CLEAR_TIMEOUT, value);
  }

  get onDiscard(): (model: AlertData) => void {
    return this.get(AlertFieldKey.ON_DISCARD);
  }

  set onDiscard(value: (model: AlertData) => void) {
    this.set(AlertFieldKey.ON_DISCARD, value);
  }

  protected setupValues(values: AlertData, exclusive: boolean): AlertData {
    if (exclusive && !(ComponentFieldKey.TYPE in values)) {
      values.type = ViewComponentKey.ALERT;
    }
    if (exclusive && !(AlertFieldKey.IS_CLOSABLE in values)) {
      values.isClosable = true;
    }
    return super.setupValues(values, exclusive) as AlertData;
  }

}
